/**
 * GTM web + Meta Pixel loader (Join). Axon stays deferred.
 *
 * Conversion path stays GTM-owned:
 *   pushMetaEvent → dataLayer → Pixel_Event (fbq) + GA4 → sGTM/Stape CAPI
 * PageView stays GTM-owned (fbAutoPageView false) so we do not double-fire
 * vs the web container Pixel tag.
 */
export type MarketingTagsConfig = {
    gtmId?: string;
    fbPixelIds?: string[];
    fbAutoPageView?: boolean;
    axonEventKey?: string;
};

export type MarketingTagsScheduleOptions = {
    interactionDelayMs?: number;
    fallbackMs?: number;
};

type FbqStub = ((...args: any[]) => void) & {
    callMethod?: (...args: any[]) => void;
    queue: IArguments[];
    push: FbqStub;
    loaded: boolean;
    version: string;
};

type AxonStub = ((...args: any[]) => void) & {
    performOperation?: (...args: any[]) => void;
    operationQueue: IArguments[];
    ts: number;
    eventKey?: string;
};

declare global {
    interface Window {
        dataLayer: any[];
        fbq?: FbqStub;
        _fbq?: FbqStub;
        axon?: AxonStub;
    }
}

const FB_SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const AXON_SCRIPT_SOURCES = [
    'https://s.axon.ai/pixel.js',
    'https://res4.applovin.com/p/l/loader.iife.js',
] as const;
const DEFAULT_INTERACTION_DELAY_MS = 2000;
const DEFAULT_FALLBACK_MS = 15_000;

let config: MarketingTagsConfig = {};
let loadPromise: Promise<void> | null = null;
let axonLoadPromise: Promise<void> | null = null;
let scheduleCancel: (() => void) | null = null;
let scheduled = false;
let axonScheduled = false;
let loadCompleted = false;
const loadedListeners = new Set<() => void>();
const bootstrappedGtmIds = new Set<string>();

function getStoredAdvancedMatchingData(): { em: string } | null {
    try {
        if (typeof localStorage === 'undefined') return null;
        const stored = JSON.parse(localStorage.getItem('funnel-v3-storage') || '{}');
        const email = stored?.state?.data?.email;
        if (email && typeof email === 'string') {
            return { em: email.toLowerCase().trim() };
        }
    } catch {
        /* ignore */
    }
    return null;
}

export function configureMarketingTags(partial: MarketingTagsConfig): void {
    config = {
        ...config,
        ...partial,
        fbPixelIds: partial.fbPixelIds ?? config.fbPixelIds,
    };
}

export function ensureDataLayer(): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
}

export function ensureFbqStub(): void {
    if (typeof window === 'undefined' || window.fbq) return;

    const stub: FbqStub = function (..._args: any[]) {
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;
        if (stub.callMethod) {
            stub.callMethod.apply(stub, args as any);
        } else {
            stub.queue.push(args);
        }
    } as FbqStub;

    stub.push = stub;
    stub.loaded = true;
    stub.version = '2.0';
    stub.queue = [];
    window.fbq = stub;
    if (!window._fbq) window._fbq = stub;
}

export function ensureAxonStub(eventKey?: string): void {
    if (typeof window === 'undefined') return;

    if (!window.axon) {
        const stub: AxonStub = function (..._args: any[]) {
            // eslint-disable-next-line prefer-rest-params
            const args = arguments;
            if (stub.performOperation) {
                stub.performOperation.apply(stub, args as any);
            } else {
                stub.operationQueue.push(args);
            }
        } as AxonStub;
        stub.operationQueue = [];
        stub.ts = Date.now();
        window.axon = stub;
    }

    if (eventKey) {
        window.axon.eventKey = eventKey;
    }
}

function injectScript(src: string, attrs?: Record<string, string>): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof document === 'undefined') {
            resolve();
            return;
        }
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = src;
        if (attrs) {
            Object.entries(attrs).forEach(([key, value]) => {
                script.setAttribute(key, value);
            });
        }

        let settled = false;
        const finish = (callback: () => void) => {
            if (settled) return;
            settled = true;
            callback();
        };
        script.onload = () => finish(resolve);
        script.onerror = () => finish(() => {
            script.remove();
            reject(new Error(`Failed to load ${src}`));
        });

        const first = document.getElementsByTagName('script')[0];
        if (first?.parentNode) {
            first.parentNode.insertBefore(script, first);
        } else {
            document.head.appendChild(script);
        }

        if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
            finish(resolve);
        }
    });
}

function queueFacebookInit(): void {
    ensureFbqStub();
    const ids = (config.fbPixelIds || []).filter(Boolean);
    if (ids.length === 0 || !window.fbq) return;

    const storedUserData = getStoredAdvancedMatchingData();
    for (const id of ids) {
        window.fbq('set', 'autoConfig', false, id);
        window.fbq('init', id, storedUserData || undefined);
    }
    if (config.fbAutoPageView) {
        window.fbq('track', 'PageView');
    }
}

function queueAxonInit(): void {
    const eventKey = config.axonEventKey?.trim();
    if (!eventKey) return;
    ensureAxonStub(eventKey);
    window.axon?.('init');
}

function injectGtm(gtmId: string): Promise<void> {
    ensureDataLayer();
    if (!bootstrappedGtmIds.has(gtmId)) {
        bootstrappedGtmIds.add(gtmId);
        window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    }

    return injectScript(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`, {
        'data-mc-marketing': 'gtm',
    }).then(() => {
        if (typeof document === 'undefined') return;
        if (document.querySelector(`noscript[data-mc-marketing="gtm"]`)) return;

        const noscript = document.createElement('noscript');
        noscript.setAttribute('data-mc-marketing', 'gtm');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
    });
}

function notifyLoaded(): void {
    loadCompleted = true;
    loadedListeners.forEach((listener) => {
        try {
            listener();
        } catch {
            /* non-blocking */
        }
    });
    loadedListeners.clear();
}

export function loadMarketingTags(): Promise<void> {
    if (loadPromise) return loadPromise;

    const hasGtm = !!config.gtmId;
    const hasMetaPixel = (config.fbPixelIds || []).some(Boolean);
    if (!hasGtm && !hasMetaPixel) return Promise.resolve();

    loadPromise = (async () => {
        if (typeof window === 'undefined') return;

        ensureDataLayer();
        queueFacebookInit();

        const tasks: Promise<void>[] = [];
        if (config.gtmId) {
            tasks.push(injectGtm(config.gtmId));
        }
        if (hasMetaPixel) {
            tasks.push(injectScript(FB_SCRIPT_SRC, { 'data-mc-marketing': 'fbq' }));
        }

        await Promise.all(tasks);
        notifyLoaded();
    })().catch((error) => {
        loadPromise = null;
        loadCompleted = false;
        throw error;
    });

    return loadPromise;
}

function loadAxonTags(): Promise<void> {
    if (axonLoadPromise) return axonLoadPromise;
    const eventKey = config.axonEventKey?.trim();
    if (!eventKey) return Promise.resolve();

    axonLoadPromise = (async () => {
        if (typeof window === 'undefined') return;
        queueAxonInit();
        await Promise.all(
            AXON_SCRIPT_SOURCES.map((src) =>
                injectScript(src, { 'data-mc-marketing': 'axon' }),
            ),
        );
    })().catch((error) => {
        axonLoadPromise = null;
        throw error;
    });

    return axonLoadPromise;
}

function scheduleAxonTags(options: MarketingTagsScheduleOptions = {}): void {
    if (typeof window === 'undefined') return;
    if (axonLoadPromise || axonScheduled) return;
    if (!config.axonEventKey?.trim()) return;

    const interactionDelayMs =
        options.interactionDelayMs ?? DEFAULT_INTERACTION_DELAY_MS;
    const fallbackMs = options.fallbackMs ?? DEFAULT_FALLBACK_MS;
    axonScheduled = true;

    let interactionTimerId: number | undefined;
    let fallbackTimerId: number | undefined;
    let interactionSeen = false;

    const cleanup = () => {
        if (typeof interactionTimerId === 'number') {
            window.clearTimeout(interactionTimerId);
        }
        if (typeof fallbackTimerId === 'number') {
            window.clearTimeout(fallbackTimerId);
        }
        window.removeEventListener('pointerdown', onFirstInteraction);
        window.removeEventListener('keydown', onFirstInteraction);
        window.removeEventListener('touchstart', onFirstInteraction);
    };

    const beginLoad = () => {
        cleanup();
        scheduleCancel = null;
        void loadAxonTags().catch((error) => {
            console.error('[marketingTags] Axon deferred load failed', error);
        });
    };

    function onFirstInteraction() {
        if (interactionSeen) return;
        interactionSeen = true;
        interactionTimerId = window.setTimeout(beginLoad, interactionDelayMs);
    }

    window.addEventListener('pointerdown', onFirstInteraction, { once: true, passive: true });
    window.addEventListener('keydown', onFirstInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', onFirstInteraction, { once: true, passive: true });
    fallbackTimerId = window.setTimeout(beginLoad, fallbackMs);

    scheduleCancel = cleanup;
}

export function scheduleMarketingTags(
    options: MarketingTagsScheduleOptions = {},
): () => void {
    if (typeof window === 'undefined') return () => undefined;
    if (!loadPromise && !scheduled) {
        scheduled = true;
        void loadMarketingTags().catch((error) => {
            console.error('[marketingTags] Attribution load failed', error);
        });
    }
    scheduleAxonTags(options);
    return () => undefined;
}

export function ensureMarketingTagsReady(): Promise<void> {
    return loadMarketingTags();
}

export function onMarketingTagsLoaded(listener: () => void): () => void {
    if (loadCompleted) {
        listener();
        return () => undefined;
    }
    loadedListeners.add(listener);
    return () => {
        loadedListeners.delete(listener);
    };
}

export function __resetMarketingTagsForTests(): void {
    config = {};
    loadPromise = null;
    axonLoadPromise = null;
    scheduleCancel?.();
    scheduleCancel = null;
    scheduled = false;
    axonScheduled = false;
    loadCompleted = false;
    loadedListeners.clear();
    bootstrappedGtmIds.clear();
    if (typeof window !== 'undefined') {
        delete window.fbq;
        delete window._fbq;
        delete window.axon;
        if (window.dataLayer) window.dataLayer.length = 0;
    }
}
